import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { Plus } from 'react-bootstrap-icons';
import { getSubjectStyle } from '../utils/subjectStyles';
import { getClassStyle } from '../utils/classStyles';

const DAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());

function ClassBadge({ classId, classes }) {
  const st = getClassStyle(classId, classes);
  if (!st) return null;
  return (
    <span className="mono" style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 9.5, fontWeight: 700, color: st.ink, background: st.tint,
      border: `1px solid color-mix(in srgb, ${st.ink} 35%, transparent)`,
      borderRadius: 100, padding: '1px 7px', whiteSpace: 'nowrap',
    }}>
      {st.name}
    </span>
  );
}

// Recurring weekly-timetable editor. A 5-day × slot grid; each cell holds an
// optional entry (subject + class + duration), persisted per change to
// /api/schedule (only the calling teacher's entries are replaced server-side).
function ScheduleEditor({ subjects, classes, maxLessons, lessonTimes, authHdr, teacherId, isMobile }) {
  const [entries, setEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null); // existing entry or null
  const [cell, setCell]         = useState({ dayIndex: 0, slotIndex: 0 });
  const [form, setForm]         = useState({ subjectId: 'math', classId: '', duration: 1 });

  useEffect(() => {
    axios.get('/api/schedule')
      .then(r => setEntries(r.data.filter(e => e.teacherId === teacherId)))
      .catch(() => setEntries([]));
  }, [teacherId]);

  const slots = Array.from({ length: maxLessons }, (_, i) => i);

  function isCovered(di, si) {
    if (si === 0) return false;
    return entries.some(e => e.dayIndex === di && e.slotIndex === si - 1 && (e.duration || 1) >= 2);
  }

  async function persist(next) {
    setEntries(next);
    try { await axios.put('/api/schedule', { entries: next }, { headers: authHdr }); }
    catch (e) { console.error(e); }
  }

  function openCell(di, si) {
    const existing = entries.find(e => e.dayIndex === di && e.slotIndex === si);
    setEditing(existing || null);
    setCell({ dayIndex: di, slotIndex: si });
    setForm(existing
      ? { subjectId: existing.subjectId, classId: existing.classId || '', duration: existing.duration || 1 }
      : { subjectId: subjects[0]?.id || 'math', classId: '', duration: 1 });
    setShowModal(true);
  }

  function handleSave() {
    const entry = {
      id: editing?.id || newId(),
      teacherId,
      dayIndex: cell.dayIndex,
      slotIndex: cell.slotIndex,
      duration: form.duration === 2 ? 2 : 1,
      subjectId: form.subjectId,
      classId: form.classId || null,
    };
    const next = editing
      ? entries.map(e => e.id === editing.id ? entry : e)
      : [...entries, entry];
    persist(next);
    setShowModal(false); setEditing(null);
  }

  function handleRemove() {
    if (!editing) return;
    persist(entries.filter(e => e.id !== editing.id));
    setShowModal(false); setEditing(null);
  }

  return (
    <>
      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: '0 0 18px', maxWidth: 640 }}>
        Lege deinen wiederkehrenden Stundenplan fest. In Wochen ohne erfasste Lektionen wird
        dieser Plan in der öffentlichen Ansicht als Vorschau angezeigt (nur Fach und Klasse).
      </p>

      <div className="card-flat scroll-x" style={{ padding: 0, overflow: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(5, 1fr)', minWidth: 480 }}>
          <div style={{ gridRow: 1, gridColumn: 1, borderBottom: '1px solid var(--line)' }} />
          {DAYS_LONG.map((d, i) => (
            <div key={d} style={{ gridRow: 1, gridColumn: i + 2, padding: '10px 12px', borderBottom: '1px solid var(--line)', borderLeft: '1px solid var(--line-soft)' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{isMobile ? d.slice(0, 2) : d}</div>
            </div>
          ))}
          {slots.map((si) => (
            <React.Fragment key={si}>
              <div className="mono" style={{
                gridRow: si + 2, gridColumn: 1,
                fontSize: 9, color: 'var(--ink-3)', padding: '6px 4px',
                borderBottom: si < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
              }}>
                {lessonTimes[si]
                  ? <><span>{lessonTimes[si].start}</span><span style={{ color: 'var(--ink-4)' }}>{lessonTimes[si].end}</span></>
                  : <span>{si + 1}</span>}
              </div>
              {Array.from({ length: 5 }, (_, di) => {
                if (isCovered(di, si)) return null;
                const entry    = entries.find(e => e.dayIndex === di && e.slotIndex === si);
                const duration = entry?.duration || 1;
                return (
                  <div key={di} style={{
                    gridRow: duration >= 2 ? `${si + 2} / span 2` : si + 2,
                    gridColumn: di + 2,
                    padding: 6, minHeight: 64,
                    borderLeft: '1px solid var(--line-soft)',
                    borderBottom: (si + duration - 1) < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
                  }}>
                    {entry ? (() => {
                      const s = getSubjectStyle(entry.subjectId, subjects);
                      return (
                        <div
                          onClick={() => openCell(di, si)}
                          style={{
                            background: s.tint, padding: '6px 8px', borderRadius: 6,
                            borderLeft: `3px solid ${s.ink}`, height: '100%', cursor: 'pointer',
                          }}
                        >
                          <div className="mono" style={{ fontSize: 9, fontWeight: 700, color: s.ink, marginBottom: 4 }}>
                            {s.short}
                          </div>
                          {entry.classId && <ClassBadge classId={entry.classId} classes={classes} />}
                        </div>
                      );
                    })() : (
                      <button
                        onClick={() => openCell(di, si)}
                        style={{
                          width: '100%', height: '100%', minHeight: 52,
                          border: '1px dashed var(--line-soft)', background: 'transparent',
                          borderRadius: 6, color: 'var(--ink-4)', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <Modal show={showModal} onHide={() => { setShowModal(false); setEditing(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? 'Stundenplan-Eintrag bearbeiten' : 'Stundenplan-Eintrag'} — {DAYS_LONG[cell.dayIndex]}
            {lessonTimes[cell.slotIndex] ? `, ${lessonTimes[cell.slotIndex].start}` : `, L${cell.slotIndex + 1}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fach</Form.Label>
              <Form.Select value={form.subjectId} onChange={e => setForm({ ...form, subjectId: e.target.value })}>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Klasse</Form.Label>
              <Form.Select value={form.classId} onChange={e => setForm({ ...form, classId: e.target.value })}>
                <option value="">— Alle Klassen —</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Dauer</Form.Label>
              <Form.Select value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) })}>
                <option value={1}>1 Lektion</option>
                <option value={2}>2 Lektionen (Doppelstunde)</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editing && (
            <Button variant="danger" className="me-auto" onClick={handleRemove}>Entfernen</Button>
          )}
          <Button variant="secondary" onClick={() => { setShowModal(false); setEditing(null); }}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSave}>Speichern</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ScheduleEditor;
